import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as websiteService from '../../services/websiteService';
import logger from '../../utils/logger';
import storage from '../../utils/LocalStorageManager';
import WebsiteUtils from '../../utils/websiteUtils';

// 创建一个store引用，在异步操作中使用
let storeRef = null;

// 导出设置store引用的函数，在store创建后调用
export const setStoreRef = (store) => {
  storeRef = store;
};

/**
 * 安全的 dispatch 函数，用于异步操作中
 * 避免在 Promise 中直接访问已撤销的 state 代理对象
 * @param {string} navId - 导航 ID
 * @param {Array} websites - 网站数据
 * @param {boolean} skipSync - 是否触发事件
 */
const safeDispatchUpdate = (navId, websites, skipSync = true) => {
  if (!storeRef) {
    logger.error('storeRef 未设置，无法安全 dispatch');
    return;
  }
  
  try {
    storeRef.dispatch(updateNavWebsites({
      navId,
      websites,
      skipSync,
      from: 'safeDispatchUpdate'
    }));
  } catch (error) {
    logger.error('安全 dispatch 失败:', error);
  }
};


// 保存每个导航的最新数据，作为临时缓存
const navCache = {};

/**
 * 获取上次选择的导航
 */
export const getLastSelectedNav = createAsyncThunk(
  'website/getLastSelectedNav',
  async (_, { rejectWithValue }) => {
    try {
      return storage.getLastSelectedNav();
    } catch (error) {
      logger.error('获取上次选择的导航失败:', error);
      return rejectWithValue('获取上次选择的导航失败');
    }
  }
);

/**
 * 获取导航的网站数据
 */
const getWebsitesForNav = (navId) => {
  try {
    const websites = storage.getWebsitesForNav(navId);
    if (websites === null) {
      return { navId, websites: [] };
    }
    return { navId, websites };
  } catch (error) {
    logger.error(`获取导航 ${navId} 的数据失败:`, error);
    return { navId, websites: [] };
  }
};

/**
 * 保存导航的网站数据
 */
const saveWebsitesForNav = (navId, websites) => {
  try {
    return storage.saveWebsitesForNav(navId, websites);
  } catch (error) {
    logger.error(`保存导航 ${navId} 的数据失败:`, error);
    return false;
  }
};

// 从API获取指定导航的网站数据
export const fetchNavWebsitesFromApi = createAsyncThunk(
  'website/fetchNavWebsitesFromApi',
  async (navId, { rejectWithValue }) => {
    try {
      logger.log(`尝试从API获取导航 ${navId} 的数据`);
      const websites = await websiteService.fetchNavWebsites(navId);
      
      if (Array.isArray(websites) && websites.length > 0) {
        logger.log(`成功从API获取导航 ${navId} 的数据，数量: ${websites.length}`);
        
        // 更新内存缓存
        navCache[navId] = websites;
        
        return {
          navId,
          websites: websites
        };
      }
      return null;
    } catch (error) {
      logger.error(`从API获取导航 ${navId} 的网站数据失败:`, error);
      return rejectWithValue(`从API获取导航 ${navId} 的网站数据失败`);
    }
  }
);

// 异步获取指定导航的网站数据
export const fetchNavWebsites = createAsyncThunk(
  'website/fetchNavWebsites',
  async (navId, { dispatch, rejectWithValue }) => {
    try {
      // 1. 首先尝试从API获取数据
      const apiResult = await dispatch(fetchNavWebsitesFromApi(navId)).unwrap();
      if (apiResult) {
        logger.log(`成功从API加载导航 ${navId} 的数据`);
        return apiResult;
      }
      
      // 2. 如果API没有数据，则尝试从内存缓存读取
      if (navCache[navId]) {
        logger.log(`API没有返回数据，从内存缓存读取导航 ${navId} 的数据，数量: ${navCache[navId].length}`);
        return {
          navId,
          websites: navCache[navId]
        };
      }

      // 3. 如果没有缓存数据，则从 storage 读取
      logger.log(`尝试从 storage 读取导航 ${navId} 的数据`);
      return getWebsitesForNav(navId);
      
    } catch (error) {
      logger.error(`获取导航 ${navId} 的网站数据失败:`, error);
      return rejectWithValue(`获取导航 ${navId} 的网站数据失败`);
    }
  }
);

// 同步导航网站数据到API
const syncNavWebsitesToApi = async (navId, websites) => {
  try {
    const updatedWebsites = await websiteService.reorderWebsites(navId, websites);
    // 如果API返回了数据，并且是数组，则返回更新后的数组（包含服务端ID）
    if (Array.isArray(updatedWebsites) && updatedWebsites.length > 0) {
      return updatedWebsites;
    }
    // 如果返回null，说明用户未登录，这是正常情况，返回原始数组
    if (updatedWebsites === null) {
      logger.log(`用户未登录，导航 ${navId} 的网站顺序仅在本地保存，未同步到API`);
      return websites;
    }
    return websites; // 其他情况返回原始数组
  } catch (error) {
    logger.error(`同步导航 ${navId} 的网站数据到API失败:`, error);
    return websites; // 失败时返回原始数组
  }
};

const initialState = {
  websitesByNav: {},
  loading: false,
  error: null,
  lastSelectedNav: (() => {
    try {
      return storage.getLastSelectedNav();
    } catch (error) {
      logger.error('读取上次选择的导航失败:', error);
      return 'home';
    }
  })()
};

const websiteSlice = createSlice({
  name: 'website',
  initialState,
  reducers: {
    setAllWebsites: (state, action) => {
      // 更新所有导航的网站数据
      state.websitesByNav = action.payload;
      // 保存到本地存储
      Object.entries(action.payload).forEach(([navId, websites]) => {
        saveWebsitesForNav(navId, websites);
      });
      
      // 更新内存缓存
      Object.assign(navCache, action.payload);
      
      logger.log('成功更新所有导航的网站数据');
    },
    setLastSelectedNav: (state, action) => {
      const navId = action.payload;
      state.lastSelectedNav = navId;
      try {
        storage.setLastSelectedNav(navId);
      } catch (error) {
        logger.error('保存上次选择的导航失败:', error);
      }
    },
    updateNavWebsites: (state, action) => {
      const { navId, websites, skipSync = false, from } = action.payload;
      
      // 更新 Redux 状态
      logger.log('updateNavWebsites from',from);
      state.websitesByNav = {
        ...state.websitesByNav,
        [navId]: websites
      };
      
      // 同时更新内存缓存，保证缓存与当前状态一致
      navCache[navId] = websites;
        
      // 记录到本地存储
      saveWebsitesForNav(navId, websites);
      
      // 如果设置了跳过同步，则不执行同步操作
      if (skipSync) {
        return;
      }
      
      // 创建一个副本以在异步操作中使用，避免使用已撤销的代理
      const websitesCopy = [...websites];
      const navIdCopy = navId;
      
      // 使用防抖进行同步，避免频繁调用
      if (window.syncTimeout) {
        clearTimeout(window.syncTimeout);
      }
      
      window.syncTimeout = setTimeout(() => {
        console.log('删除',websitesCopy);
        // 同步到API并处理返回结果
        syncNavWebsitesToApi(navIdCopy, websitesCopy).then(updatedWebsites => {
          try {
            // 如果服务端返回了不同的数据，通过dispatch更新本地状态
            if (updatedWebsites && JSON.stringify(updatedWebsites) !== JSON.stringify(websitesCopy)) {
              // 使用dispatch来更新状态，但跳过再次同步
              safeDispatchUpdate(navIdCopy, updatedWebsites, true);
              
              logger.log(`使用服务端返回的排序结果更新导航 ${navIdCopy} 的网站数据`);
              
              // 触发额外的事件通知ID可能已更新
              window.dispatchEvent(new CustomEvent('navWebsitesUpdated', {
                detail: { navId: navIdCopy }
              }));
            }
          } catch (error) {
            logger.error(`处理API返回数据时出错:`, error);
          }
        }).catch(error => {
          logger.error(`同步导航 ${navIdCopy} 的网站数据到API失败:`, error);
        });
      }, 1000); // 1秒的防抖延迟
    },
    addWebsite: (state, action) => {
      const { navId, website } = action.payload;
      const currentWebsites = state.websitesByNav[navId] || [];
      const websiteIdToUpdate = website.id || website.complexId;
      
      // 使用工具类处理网站插入位置
      const updatedWebsites = WebsiteUtils.insertWebsiteByType(currentWebsites, website);
      
      // 更新 Redux 状态
      state.websitesByNav = {
        ...state.websitesByNav,
        [navId]: updatedWebsites
      };
      
      // 更新内存缓存
      navCache[navId] = updatedWebsites;
      
      // 保存到本地存储
      saveWebsitesForNav(navId, updatedWebsites);
      
      // 创建副本以在异步操作中使用
      const websiteCopy = { ...website };
      const navIdCopy = navId;
      
      // 同步到API
      websiteService.addWebsite(navIdCopy, websiteCopy)
        .then(serverResponse => {
          try {
            // 检查是否有错误
            if (serverResponse && serverResponse.error) {
              logger.error(`向API添加导航 ${navIdCopy} 的网站失败: ${serverResponse.error}`);
              
              // 删除本地数据
              const currentState = storeRef.getState();
              const currentWebsites = currentState.website.websitesByNav[navIdCopy] || [];
              const updatedWebsites = currentWebsites.filter(site => {
                const siteId = site.id || site.complexId;
                return siteId !== websiteIdToUpdate;
              });
              
              // 使用 dispatch 更新状态
              safeDispatchUpdate(navIdCopy, updatedWebsites, true);
              
              // 触发失败事件，让调用方可以显示错误消息
              window.dispatchEvent(new CustomEvent('websiteAddFailed', {
                detail: { 
                  navId: navIdCopy, 
                  website: websiteCopy,
                  reason: serverResponse.error
                }
              }));
            } else if (serverResponse && (serverResponse.id !== websiteCopy.id || serverResponse.complexId !== websiteCopy.complexId)) {
              // 如果服务端返回了网站数据，并且ID与本地不同
              logger.log(`使用服务端返回的ID更新导航 ${navIdCopy} 中的网站`);
              
              // 通过 dispatch 更新状态，而不是直接修改 state
              const currentState = storeRef.getState();
              const currentWebsites = currentState.website.websitesByNav[navIdCopy] || [];
              
              const updatedWebsites = currentWebsites.map(site => {
                const siteId = site.id || site.complexId;
                return siteId === websiteIdToUpdate ? { ...site, ...serverResponse } : site;
              });
              
              // 使用 dispatch 更新状态
              safeDispatchUpdate(navIdCopy, updatedWebsites, true);
              
              // 触发成功事件，让调用方可以跟踪成功状态
              window.dispatchEvent(new CustomEvent('websiteAddSuccess', {
                detail: { 
                  navId: navIdCopy, 
                  website: websiteCopy
                }
              }));
            } else if (serverResponse && !serverResponse.error) {
              // 如果服务端返回了数据但没有错误，说明添加成功（即使ID没有变化）
              logger.log(`网站添加成功，ID未变化`);
              
              // 触发成功事件，让调用方可以跟踪成功状态
              window.dispatchEvent(new CustomEvent('websiteAddSuccess', {
                detail: { 
                  navId: navIdCopy, 
                  website: websiteCopy
                }
              }));
            } else if (serverResponse === null) {
              // 如果服务端返回null，检查是否是未登录的情况
              // 在websiteService中，未登录时会返回null，这是正常情况，不需要删除本地数据
              logger.log(`用户未登录，网站 ${websiteIdToUpdate} 仅在本地保存，未同步到API`);
              
              // 触发成功事件，因为本地保存是成功的
              window.dispatchEvent(new CustomEvent('websiteAddSuccess', {
                detail: { 
                  navId: navIdCopy, 
                  website: websiteCopy,
                  localOnly: true // 标记为仅本地保存
                }
              }));
            }
          } catch (error) {
            logger.error(`处理API返回数据时出错:`, error);
          }
        });
    },
    updateWebsite: (state, action) => {
      const { navId, websiteId, updates } = action.payload;
      const currentWebsites = state.websitesByNav[navId] || [];
      const updatedWebsites = currentWebsites.map(site => 
        (site.id === websiteId || site.complexId === websiteId) ? { ...site, ...updates } : site
      );
      
      // 更新 Redux 状态
      state.websitesByNav = {
        ...state.websitesByNav,
        [navId]: updatedWebsites
      };
      
      // 更新内存缓存
      navCache[navId] = updatedWebsites;
      
      // 保存到本地存储
      saveWebsitesForNav(navId, updatedWebsites);
      
      // 同步到API
      websiteService.updateWebsite(navId, websiteId, updates)
        .then(serverWebsite => {
          // 如果服务端返回了网站数据
          if (serverWebsite && typeof serverWebsite === 'object') {
            // 查找并应用服务端可能更新的其他字段
            const currentWebsites = state.websitesByNav[navId] || [];
            const updatedWebsites = currentWebsites.map(site => {
              if (site.id === websiteId || site.complexId === websiteId) {
                // 保留本地的一些UI状态字段，合并服务端返回的其他数据
                return { ...site, ...serverWebsite };
              }
              return site;
            });
            
            // 更新状态
            state.websitesByNav = {
              ...state.websitesByNav,
              [navId]: updatedWebsites
            };
            
            // 更新内存缓存
            navCache[navId] = updatedWebsites;
      
            // 保存到本地存储
            saveWebsitesForNav(navId, updatedWebsites);
            
            logger.log(`使用服务端返回的数据更新导航 ${navId} 下的网站 ${websiteId}`);
          }
        })
        .catch(error => logger.error(`向API更新导航 ${navId} 中的网站 ${websiteId} 失败:`, error));
    },
    deleteWebsite: (state, action) => {
      const { navId, websiteId } = action.payload;
      const currentWebsites = state.websitesByNav[navId] || [];
      const updatedWebsites = currentWebsites.filter(site => 
        site.id !== websiteId && site.complexId !== websiteId
      );
      
      // 更新 Redux 状态
      state.websitesByNav = {
        ...state.websitesByNav,
        [navId]: updatedWebsites
      };
      
      // 更新内存缓存
      navCache[navId] = updatedWebsites;
      
      // 保存到本地存储
      saveWebsitesForNav(navId, updatedWebsites);
      
      // 同步到API
      websiteService.deleteWebsite(navId, websiteId)
        .catch(error => logger.error(`向API删除导航 ${navId} 中的网站 ${websiteId} 失败:`, error));
    },
    // 初始化导航数据时重置状态
    resetLastSelectedNav: (state) => {
      state.lastSelectedNav = 'home';
      try {
        storage.setLastSelectedNav('home');
      } catch (error) {
        logger.error('重置上次选择的导航失败:', error);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取上次选择的导航
      .addCase(getLastSelectedNav.fulfilled, (state, action) => {
        state.lastSelectedNav = action.payload;
      })
      // 处理从API获取导航网站数据
      .addCase(fetchNavWebsitesFromApi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNavWebsitesFromApi.fulfilled, (state, action) => {
        if (action.payload) {
          const { navId, websites } = action.payload;
          state.websitesByNav = {
            ...state.websitesByNav,
            [navId]: websites
          };
          state.loading = false;
          
          // 更新内存缓存
          navCache[navId] = websites;
        }
      })
      .addCase(fetchNavWebsitesFromApi.rejected, (state) => {
        // 不在这里设置error状态，因为这只是多级获取的一部分
        state.loading = true;
      })
      // 处理获取导航网站数据
      .addCase(fetchNavWebsites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNavWebsites.fulfilled, (state, action) => {
        const { navId, websites } = action.payload;
        state.websitesByNav = {
          ...state.websitesByNav,
          [navId]: websites
        };
        state.loading = false;
        
        // 更新内存缓存
        navCache[navId] = websites;
      })
      .addCase(fetchNavWebsites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setAllWebsites, updateNavWebsites, setLastSelectedNav, addWebsite, updateWebsite, deleteWebsite, resetLastSelectedNav } = websiteSlice.actions;
export default websiteSlice.reducer; 